/**
 * Green Flag — PhoneField
 *
 * US-only phone input. Same value/onChange contract as before
 * (onChange receives the raw input event), restyled to match the rest
 * of the GF form vocabulary (FormInput, BirthDateInput): 52px tall,
 * 1.5px border on bg.canvas with the brand focus ring on focus-within.
 *
 * The flag + "+1" prefix sit inside the same bordered shell as the
 * input, separated by a hairline divider. The "+1" prefix uses the
 * same 15px medium type as the input itself so the two read as one
 * line.
 */

import { Box, chakra, Flex, Text } from "@chakra-ui/react";
import americanFlag from "./images/american_flag.png";

interface PhoneFieldProps {
  value?: string;
  name?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Optional inline validation message rendered below the field. */
  validationMessage?: string;
  width?: string;
  /** Compat shim for callers passing the legacy Evergreen `label` prop;
   *  rendered above the field if non-empty. */
  label?: React.ReactNode;
}

export const PhoneField = ({
  value,
  onChange,
  name,
  validationMessage,
  width = "100%",
  label,
}: PhoneFieldProps) => {
  return (
    <Box w={width}>
      {label && (
        <Text
          as="label"
          display="block"
          mb="6px"
          fontFamily="body"
          fontSize="13px"
          fontWeight="medium"
          color="fg.default"
          letterSpacing="-0.005em"
        >
          {label}
        </Text>
      )}
      <Flex
        as="label"
        align="center"
        w="100%"
        h="52px"
        bg="bg.canvas"
        borderRadius="md"
        borderWidth="1.5px"
        borderStyle="solid"
        borderColor="border.default"
        transition="border-color 120ms, box-shadow 120ms"
        _focusWithin={{
          borderColor: "border.brand",
          boxShadow: "0 0 0 4px {colors.bg.brandTint}",
        }}
        overflow="hidden"
      >
        <Box pl="14px" pr="10px" display="flex" alignItems="center">
          <chakra.img
            src={americanFlag}
            alt="United States"
            w="22px"
            h="auto"
            display="block"
          />
        </Box>
        <Flex
          align="center"
          h="100%"
          pl="10px"
          pr="10px"
          borderLeftWidth="1px"
          borderLeftStyle="solid"
          borderLeftColor="border.default"
          fontFamily="body"
          fontSize={{ base: "16px", md: "15px" }}
          fontWeight="medium"
          color="fg.default"
          letterSpacing="-0.005em"
        >
          +1
        </Flex>
        <chakra.input
          type="tel"
          name={name}
          value={value || ""}
          onChange={onChange}
          placeholder="(000) 000 - 0000"
          flex="1"
          minW="0"
          h="100%"
          px="10px"
          bg="transparent"
          border="0"
          outline="none"
          fontFamily="body"
          // 16px on mobile keeps iOS Safari from auto-zooming on focus.
          fontSize={{ base: "16px", md: "15px" }}
          fontWeight="medium"
          color="fg.default"
          letterSpacing="-0.005em"
          _placeholder={{ color: "fg.muted" }}
        />
      </Flex>
      {validationMessage && (
        <Text mt="6px" fontSize="12px" color="bg.danger" fontFamily="body">
          {validationMessage}
        </Text>
      )}
    </Box>
  );
};
